import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Reporter de errores · TODO sprint observabilidad: integrar Sentry o
 * equivalente. Por ahora log dev-only + window listeners para capturar
 * errores async que no llegan al ErrorBoundary de React.
 *
 * Mantenemos un solo punto de entrada (`reportError`) para que la
 * integracion futura sea un solo cambio.
 */
function reportError(error: unknown, context: Record<string, unknown> = {}): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error('[reportError]', error, context);
  }
  // TODO: Sentry.captureException(error, { extra: context });
}

// Listeners globales para errores async (promesas no manejadas + errores
// que escapan al render tree). Se instalan una sola vez · idempotente.
let listenersInstalled = false;
function ensureGlobalListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}

// Error boundary requiere class component (React no expone hook equivalente).
// Excepcion consciente a la regla "functional only" del scope.
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    ensureGlobalListeners();
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      source: 'ErrorBoundary',
      componentStack: info.componentStack,
    });
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      const message = this.state.error.message || 'Error inesperado en la interfaz.';
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 text-center"
        >
          <p className="mb-2 text-4xl" aria-hidden="true">
            ⚠️
          </p>
          <h1 className="mb-2 text-xl font-semibold">Algo se ha roto</h1>
          <p className="mb-1 text-sm text-ink/70">
            Hemos tenido un problema cargando esta pantalla.
          </p>
          <p className="mb-6 text-xs text-ink/40">
            Si el problema persiste contacta con soporte.
          </p>
          {import.meta.env.DEV && (
            <pre className="mb-4 max-w-full overflow-auto rounded-md border border-ink/10 bg-ink/5 p-3 text-left text-[11px] text-red-700 dark:text-red-200">
              {message}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="btn-primary"
              aria-label="Recargar la pagina completa"
            >
              Recargar página
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="btn-secondary"
              aria-label="Reintentar sin recargar"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
